import { useCallback, useEffect, useMemo, useContext, useState } from 'react';
import _ from 'underscore';
import { useSelector } from '@xstate/react';
import { isCanceling, stateLoggingListener, StepEvents, isSaving } from '@bingads-webui-campaign-react/step-wizard-machine';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { useExitWithoutSavingConfirmation } from '@bingads-webui-campaign-react/step-wizard';
import { useImpulse, usePrevProp } from '@bingads-webui-campaign-react/step-wizard-controls';
import { serverErrorsCompare } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';

import { needsSaving } from './step-wizard-config';

const doesNeedSavingSelector = state => !isCanceling(state) && needsSaving(state.context) && !isSaving(state);
const hideStepHeaderSelector = state => state.context.stepInfo.hideStepHeader;
const adsSelector = state => state.context.data.ads;
const adsErrorsSelector = state => state.context.data.adsErrors;
const serverErrorsSelector = state => state.context.data.serverErrors;
const otherErrorsSelector = state => state.context.data.otherErrors;
const systemErrorsSelector = state => state.context.data.systemErrors;
const invalidSaveAttemptSelector = state => state.context.invalidSaveAttempt;
const dealSelector = state => state.context.data.deal;
const biddngSchemeSelector = state => state.context.data.biddingScheme;

export const useStepAdsSettingsState = (stepService, activity) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  useEffect(() => {
    const subscription = stepService.subscribe(stateLoggingListener);
    return subscription.unsubscribe;
  }, [stepService]);

  const hideStepHeader = useSelector(stepService, hideStepHeaderSelector);

  const ads = useSelector(stepService, adsSelector);

  const doesNeedSaving = useSelector(stepService, doesNeedSavingSelector);
  const adsErrors = useSelector(stepService, adsErrorsSelector);
  const otherErrors = useSelector(stepService, otherErrorsSelector);
  const systemErrors = useSelector(stepService, systemErrorsSelector);
  const serverErrors = useSelector(stepService, serverErrorsSelector);
  const invalidSaveAttempt = useSelector(stepService, invalidSaveAttemptSelector);
  const deal = useSelector(stepService, dealSelector);
  const biddingScheme = useSelector(stepService, biddngSchemeSelector);

  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(doesNeedSaving, stepService, i18n);

  const removeEmptyAds = newAdsData => newAdsData.filter(ad => !_.isEmpty(ad.data));

  const onAdsValidation = useCallback((results) => {
    const { data: newAdsData, ...restResults } = results;

    const newAds = removeEmptyAds(newAdsData);

    const dataPatch = {
      ads: {
        ...ads,
        data: [
          ...newAds,
        ],
        ...restResults,
      },
      activity,
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, ads, activity]);

  // eslint-disable-next-line no-bitwise
  const [, hasInvalidSaveAttemptChanged] = usePrevProp(invalidSaveAttempt | 0);
  const [, hasServerErrorsChanged] = usePrevProp(serverErrors, serverErrorsCompare);
  const areBeaconsActive = useImpulse(hasInvalidSaveAttemptChanged || hasServerErrorsChanged);

  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false);
  if (hasInvalidSaveAttemptChanged) {
    setShowAllValidationErrors(true);
  }

  const state = {
    ads,
    i18n,
    legacyI18n,
    hideStepHeader,
    adsErrors,
    otherErrors,
    systemErrors,
    navigationConfirmationPrompt,
    showAllValidationErrors,
    areBeaconsActive,
    serverErrors,
    deal,
    biddingScheme,
  };
  const api = { onAdsValidation };

  return [state, api];
};
